import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IRecommendOrderArrival } from '@/recommendOrderModule/domains/entities/recommendOrderArrival';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { IExtraSimulate } from '@/recommendOrderModule/pages/interfaces/extraSimulation';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { AggregateBusinessFilterProperty, FilterType } from '@/coreDomainsModule/entities/filter';
import { IRecommendOrderErrorType } from './recommendOrderError';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IRelatedTransactionsDetail } from '@/recommendOrderModule/domains/entities/relatedTransaction';
import { ILongTermCustomerForecastResult } from '@/recommendOrderModule/domains/entities/longTermCustomerForecast';

/**
 * 将来の月次計算結果の行データ型
 */
export interface IFutureMonthlyCalculationsRow {
  year: number;
  month: number;
  endOfMonthInventory?: number; // 予想末在庫
  inventoryTurnoverRate?: number; // 予想在庫回転率
  forecastAmount?: number; // 内示 (supplierForecastAmount)
  salesResult: string; // 販売実績 (常に "-")
}

/**
 * 発注情報（シミュレーションで使用する）
 * @property isLocked - ロックされているかどうか
 */
export interface IRecommendOrderArrivalForRecommendEdit extends IRecommendOrderArrival {
  partialDeliveries: IArrival[] | undefined; // 分納情報
  isLocked: boolean; // ロックされているかどうか
  isChecked: boolean; // チェックされているかどうか
  isExpanded: boolean; // 展開されているかどうか
}
// IRecommendOrderArrivalForRecommendEditのキーを追加したら必ずここも追加させること！！！
const arrivalForRecommendEditsProperties = ['isLocked', 'isChecked'];

/**
 * 画面遷移時に受け取るパラメータ
 */
export interface RecommendOrderArgs {
  businessId: string;
}
/**
 * IRecommendOrderArrivalForRecommendEdit -> IRecommendOrderArrivalにアップキャストする際に不要なプロパティを削ぎ落とす
 * @param arrivalForRecommendEdit - 取引一覧
 * @returns　取引一覧
 */
export function arrivalForRecommendEditToIRecommendOrderArrival(
  arrivalForRecommendEdit: IRecommendOrderArrivalForRecommendEdit[]
): IRecommendOrderArrival[] {
  for (const arrival of arrivalForRecommendEdit) {
    arrivalForRecommendEditsProperties.forEach((key) => {
      delete arrival[key];
    });
  }
  return arrivalForRecommendEdit;
}

/**
 *　シミュレーションの結果をシミュレーターに反映させる
 */
export type UpdateSimulate = (
  customerAnnounce?: IAnnounce[],
  stdInventoryIndices?: StdInventoryIndex,
  leadTime?: number,
  recommend?: IRecommendOrderArrival[],
  startDate?: Date
) => void; // シミュレーションを更新する

/**
 * 編集状況
 */
export type RecommendEditProgress = 'EditReceiveAnnounce' | 'EditRecommend' | 'EditSupplierAnnounce' | 'EditCompleted' | 'Other';

/**
 * UIState
 * @property formSet - シミュレーション本体
 * @property progress - 現在の編集状況
 * @property recommend - 編集用の推奨発注情報
 * @property supplierAnnounce - 編集用の仕入先内示情報
 * @property extraSimulateInfos - 編集用の推奨発注情報を元に、シミュレーションを行うための情報
 * @property renderRecommendEditKey - 発注推奨画面を再レンダリングするためのキー
 * @property renderOrderFormKey - シミュレート画面を再レンダリングするためのキー * @property announceAdjustmentRate - 内示調整率
 */
export interface IBusinessDetail {
  /**
   * シミュレーション本体
   */
  formSet: IBusiness;

  /**
   * 客先内示の過去情報
   * NOTE: 一旦シミュレーションで使用する取引情報と別で過去の情報を保持したいので定義
   */
  customerForecastHistory: IAnnounce[];

  /**
   * ローディング状態
   */
  loadingState: LoadingState;

  /**
   * 選択中の情報
   */
  selectedInfo: AggregateBusinessFilterProperty;

  /**
   * 選択中の情報(初期状態、差分検知用)
   */
  selectedInitialInfo: AggregateBusinessFilterProperty;

  /**
   * 選択中のフィルター種別（排他制御のためのプロパティ）
   */
  isOpenFilterType?: FilterType;

  /**
   * 現在の編集状況
   */
  progress: RecommendEditProgress;
  /**
   * 編集用の推奨発注情報
   */
  recommend: IRecommendOrderArrivalForRecommendEdit[];

  /**
   * 分納情報のフォーム (編集不可)
   */
  partialDeliveryForms: IArrival[]; // 分納情報のフォーム

  /**
   * 発注推奨画面を再レンダリングするためのキー
   */
  renderRecommendEditKey: number;
  /**
   * シミュレート画面を再レンダリングするためのキー
   */
  renderOrderFormKey: number;
  /**
   * 内示調整率
   */
  announceAdjustmentRate: number;
  /**
   * 編集用の仕入先内示情報
   */
  supplierAnnounce?: IAnnounce[];
  /**
   * 編集用の推奨発注情報を元に、シミュレーションを行うための情報
   */
  extraSimulateInfos?: IExtraSimulate;
  /**
   * 選択されているリードタイムの値。シミュレーションで使用する日数値ではなく表示上の値(dbに格納されている値)
   */
  selectedLeadTimeDisplayValue?: number;
  /**
   * 選択されているリードタイムの単位。シミュレーションで使用する単位(day固定)ではなく表示上の値(dbに格納されている値)
   */
  selectedLeadTimeUnit?: string;
  /**
   * デフォルトの客先内示情報
   */
  defaultCustomerAnnounce: IAnnounce[];

  /**
   * チェックされたアイテムのインデックスを格納する配列
   */
  checkedIndex: number[];

  /**
   * エラーのタイプ
   */
  errorType?: IRecommendOrderErrorType;

  /**
   * 仕入先への発注計画(RecommendEditDefinedOrder)内テーブルスクロール値
   */
  recommendEditDefinedOrderScrollPosition: {
    x: number;
    y: number;
  };

  /**
   * 当該集計に紐づく発注済み未納取引一覧
   */
  unpaidOrderedOrder: IArrival[];

  /**
   * 発注済み未納編集モーダルState
   */
  isOpeningEditUnpaidOrderedOrderModal: boolean;

  /**
   * 関連取引モーダルの表示状態
   */
  relatedTransactionsModalState: boolean;

  /**
   * 関連取引データ
   */
  relatedTransactionsData: IRelatedTransactionsDetail | null;

  /**
   * データ取得中のローディング状態
   */
  relatedTransactionsLoading: boolean;

  /**
   * エラーメッセージ
   */
  relatedTransactionsError: string | null;

  /**
   * 関連取引モーダル用の受領月オフセット
   */
  relatedTransactionsReceiptMonthOffset: number;
  /**
   * 長期客先内示データ (raw data)
   */
  longTermCustomerForecastData?: ILongTermCustomerForecastResult;
  /**
   * 将来の月次計算結果（予想末在庫、予想在庫回転率、内示、販売実績）
   */
  futureMonthlyCalculations: IFutureMonthlyCalculationsRow[];
}
